import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import ProjectModal from "../components/ProjectModal";
import { ProjectsInfo } from "../utils/constant";
import { motion } from "framer-motion";
import fallbackImage from "../images/web-dev.svg";

const PortfolioPage = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Transform ProjectsInfo to match the expected format
  const projects = useMemo(() => {
    return ProjectsInfo.map((project, index) => ({
      id: index + 1,
      title: project.ProjectName,
      description: project.ProjectDescription,
      tech: project.TechUsed || [],
      image:
        project.Images && project.Images.length > 0
          ? project.Images[0]
          : fallbackImage,
      images: project.Images || [],
      videos: project.Video || [],
      category: project.Category
    }));
  }, []);

  const openModal = (project) => {
    setSelectedProject(project);
  };

  const closeModal = () => {
    setSelectedProject(null);
  };

  const aiProjects = projects.filter((item) => item.category === "ai");
  const webApps = projects.filter((item) => item.category === "web");
  const uiDesigns = projects.filter((item) => item.category === "ui_designs");

  return (
    <>
      <Helmet>
        <title>Portfolio | TeqnoDux</title>
        <meta
          name="description"
          content="Explore our portfolio of innovative software solutions and projects developed by TeqnoDux."
        />
      </Helmet>
      <NavBar isDarkMode={isDarkMode} />
      <div
        className={`pt-24 min-h-screen transition-colors duration-300 ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        {/* Toggle Button */}
        <div className="fixed bottom-14 right-4 z-10">
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`p-3 rounded-full shadow-lg transition-all duration-300 ${
              isDarkMode
                ? "bg-[#377c7d] text-white hover:bg-[#2a6162]"
                : "bg-white text-gray-800 hover:bg-gray-100"
            }`}
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            )}
          </button>
        </div>
        <div className="container mx-auto px-4 py-12">
          {/*AI-Projects Header*/}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-12">
            {/* Left Line */}
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: "100px", opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="h-[3px] bg-[#377c7d] rounded-full"
            />

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={`text-3xl sm:text-3xl font-bold text-center ${
                isDarkMode ? "text-[#4a9fa0]" : "text-[#377c7d]"
              }`}
            >
              AI Projects
            </motion.h1>

            {/* Right Line */}
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: "100px", opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="h-[3px] bg-[#377c7d] rounded-full"
            />
          </div>

          {/*AI-Projects List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {aiProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => openModal(project)}
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } rounded-lg shadow-2xl border-2 border-[#377c7d]/20 overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-[0_30px_80px_rgba(55,124,125,0.3)] hover:border-[#377c7d]/40`}
              >
                {/* Project Image */}
                <div
                  className={`relative h-56 ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-100"
                  } flex items-center justify-center`}
                >
                  <div className="w-[95%] h-[95%] overflow-visible rounded-md transition-all duration-300">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 ease-out hover:scale-110"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = fallbackImage;
                      }}
                    />
                  </div>
                </div>

                {/* Project Title */}
                <div className="px-6 py-4">
                  <h3
                    className={`text-md font-bold mb-2 ${
                      isDarkMode ? "text-gray-100" : "text-gray-800"
                    }`}
                  >
                    {project.title}
                  </h3>
                  <div
                    className={`flex items-center text-sm font-semibold ${
                      isDarkMode ? "text-[#4a9fa0]" : "text-[#377c7d]"
                    }`}
                  >
                    View Details
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/*Web-Apps Header*/}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-12">
            {/* Left Line */}
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: "100px", opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="h-[3px] bg-[#377c7d] rounded-full"
            />

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={`text-3xl sm:text-3xl font-bold text-center ${
                isDarkMode ? "text-[#4a9fa0]" : "text-[#377c7d]"
              }`}
            >
              Web Apps
            </motion.h1>

            {/* Right Line */}
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: "100px", opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="h-[3px] bg-[#377c7d] rounded-full"
            />
          </div>

          {/*Web-Apps List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {webApps.map((project) => (
              <div
                key={project.id}
                onClick={() => openModal(project)}
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } rounded-lg shadow-2xl border-2 border-[#377c7d]/20 overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-[0_20px_50px_rgba(55,124,125,0.3)] hover:border-[#377c7d]/40`}
              >
                {/* Project Image */}
                <div
                  className={`relative h-56 ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-100"
                  } flex items-center justify-center`}
                >
                  <div className="w-[95%] h-[95%] overflow-visible rounded-md transition-all duration-300">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 ease-out hover:scale-110"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = fallbackImage;
                      }}
                    />
                  </div>
                </div>

                {/* Project Title */}
                <div className="px-6 py-4">
                  <h3
                    className={`text-md font-bold mb-2 ${
                      isDarkMode ? "text-gray-100" : "text-gray-800"
                    }`}
                  >
                    {project.title}
                  </h3>
                  <div
                    className={`flex items-center text-sm font-semibold ${
                      isDarkMode ? "text-[#4a9fa0]" : "text-[#377c7d]"
                    }`}
                  >
                    View Details
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/*UI/UX-Design's Header*/}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-12">
            {/* Left Line */}
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: "140px", opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="h-[3px] bg-[#377c7d] rounded-full"
            />

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={`text-3xl sm:text-3xl font-bold text-center ${
                isDarkMode ? "text-[#4a9fa0]" : "text-[#377c7d]"
              }`}
            >
              UI/UX Design's
            </motion.h1>

            {/* Right Line */}
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: "140px", opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="h-[3px] bg-[#377c7d] rounded-full"
            />
          </div>

          {/*UI/UX-Design's List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {uiDesigns.map((project) => (
              <div
                key={project.id}
                onClick={() => openModal(project)}
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } rounded-lg shadow-2xl border-2 border-[#377c7d]/20 overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-[0_20px_50px_rgba(55,124,125,0.3)] hover:border-[#377c7d]/40`}
              >
                {/* Project Image */}
                <div
                  className={`relative h-56 ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-100"
                  } flex items-center justify-center`}
                >
                  <div className="w-[95%] h-[95%] overflow-visible rounded-md transition-all duration-300">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 ease-out hover:scale-110"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = fallbackImage;
                      }}
                    />
                  </div>
                </div>

                {/* Project Title */}
                <div className="px-6 py-4">
                  <h3
                    className={`text-md font-bold mb-2 ${
                      isDarkMode ? "text-gray-100" : "text-gray-800"
                    }`}
                  >
                    {project.title}
                  </h3>
                  <div
                    className={`flex items-center text-sm font-semibold ${
                      isDarkMode ? "text-[#4a9fa0]" : "text-[#377c7d]"
                    }`}
                  >
                    View Details
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Modal */}
      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={closeModal} />
      )}

      <Footer />
    </>
  );
};

export default PortfolioPage;
